import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAppSelector } from './hooks/redux';
import { CompanyProvider } from './features/company/CompanyContext';
import LoginPage from './pages/Login';
import SetPasswordPage from './pages/SetPassword';
import WorkspaceShell from './components/WorkspaceShell';
import AccountPage from './pages/Account';
import CustomerOverview from './pages/customer/Overview';
import MyCompanies from './pages/customer/MyCompanies';
import CompanyProfile from './pages/customer/CompanyProfile';
import Documents from './pages/customer/Documents';
import Verification from './pages/customer/Verification';
import Rating from './pages/customer/Rating';
import Ready from './pages/customer/Ready';
import Partners from './pages/customer/Partners';
import Funded from './pages/customer/Funded';
import Payments from './pages/customer/Payments';
import Messages from './pages/customer/Messages';
import EvidenceAudit from './pages/customer/EvidenceAudit';
import Help from './pages/customer/Help';

function RequireCustomer({ children }: { children: ReactNode }) {
  const { token, user } = useAppSelector((s) => s.auth);
  if (!token || (user && user.role !== 'CUSTOMER')) return <Navigate to="/login" replace />;
  if (user?.mustChangePassword) return <Navigate to="/set-password" replace />;
  return <CompanyProvider>{children}</CompanyProvider>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route
        element={
          <RequireCustomer>
            <WorkspaceShell />
          </RequireCustomer>
        }
      >
        <Route path="/" element={<MyCompanies />} />
        <Route path="/companies" element={<Navigate to="/" replace />} />
        <Route path="/overview" element={<CustomerOverview />} />
        <Route path="/company" element={<CompanyProfile />} />
        <Route path="/company/new" element={<CompanyProfile key="new" mode="new" />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/verification" element={<Verification />} />
        <Route path="/rating" element={<Rating />} />
        <Route path="/remediation" element={<Navigate to="/documents" replace />} />
        <Route path="/ready" element={<Ready />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/fundraising" element={<Navigate to="/partners" replace />} />
        <Route path="/funded" element={<Funded />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/evidence" element={<EvidenceAudit />} />
        <Route path="/help" element={<Help />} />
        <Route path="/account" element={<AccountPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
